import { Box, Flex, NoSsr, TextV2 } from '@withjoy/joykit';
import React from 'react';
import { CONTAINER_BLOCK_PADDING_X, CONTENT_BLOCK_MAX_WIDTH, FULL_BLEED_MARGIN_X, getBrannanMobileMediaQuery } from '../../LayoutAloha.theme';
import { StyledTitle } from './Page.styles';
import { PrettyLink } from '@apps/guest/packages/layout-engine/components/PrettyLink';
import { ResponsiveText } from '@apps/guest/packages/layout-engine/components/ResponsiveText';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import { body4ToSubHeadingOverride, display3ToPageTitleOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';
import type { PhotoV2WithoutBlurHashFragment } from '@graphql/fragments/photo.graphql.generated';
import type { EventPageVisibility, EventPageType } from '@graphql/generated-base';
import { Category, DesignLayoutType } from '@graphql/generated-base';
import { LazyRichText } from '@apps/guest/packages/layout-engine/widgets/Custom/components/RichText/RichText.lazy';
import { isRichTextContent } from '@shared/utils/html';
import { isRegistryPage } from '@shared/utils/migratedCustomPages';
import { useMigratedCustomPageFeatureFlags } from '@shared/hooks/useMigratedCustomPageFeatureFlags/useMigratedCustomPageFeatureFlags';
import CoverPhoto from '../../../../components/CoverPhoto';
import { useMediaQuery } from '@withjoy/joykit/utils';
import { AccommodationsCountdownExperiment } from '@apps/guest/components/AccommodationsCountdownExperiment';
import { useColorPaletteProvider } from '../../../ColorPaletteProvider/ColorPaletteProvider';
import { useShouldRevealPageContent } from '@apps/guest/packages/layout-engine/components/PageContentVisibilityProvider';
import { animationTransitionExt } from '@shared/utils/animationTransition';

// Fade chrome (title + subtitle) in for the target page only — non-target
// stacked pages stay at opacity 0 so the initial paint focuses on the
// deep-linked route. Once revealed, stays revealed (provider captures the
// initial slug in a ref).
//
// Unlike Brannan, Aloha's `CoverPhoto` and graphic accent are intentionally
// NOT faded here. Aloha only renders its photo on small screens and sits
// below other chrome, so fading additional surfaces adds noise without
// visual payoff on this layout.
const FADE_IN_TRANSITION = animationTransitionExt({ property: 'opacity' });

interface Props
  extends Readonly<{
    title?: Maybe<string>;
    subtitle?: Maybe<string>;
    graphicAccent?: Maybe<JSX.Element>;
    marginTop?: string | string[];
    isAccommodations?: boolean;
    pageSlug?: string;
    photo?: Maybe<PhotoV2WithoutBlurHashFragment>;
    pageId: string;
    pageType: EventPageType;
    visibility: EventPageVisibility;
  }> {}

export const Page = React.forwardRef<HTMLDivElement, React.PropsWithChildren<Props>>((props, pageRef) => {
  const { title, subtitle, graphicAccent, children, isAccommodations, pageSlug, photo, pageId, pageType, visibility, ...restProps } = props;

  const isSmallScreen = useMediaQuery(getBrannanMobileMediaQuery);
  const { pageFontColor } = useColorPaletteProvider();

  const { isMigratedCustomPageEnabled } = useMigratedCustomPageFeatureFlags();

  const shouldReveal = useShouldRevealPageContent();
  const isRevealed = shouldReveal({ type: pageType, pageSlug: pageSlug ?? '' });
  const chromeStyle: React.CSSProperties = { opacity: isRevealed ? 1 : 0, transition: FADE_IN_TRANSITION };

  return isAccommodations ? (
    <Flex
      ref={pageRef}
      {...restProps}
      paddingX={CONTAINER_BLOCK_PADDING_X}
      width="100%"
      justifyContent={'center'}
      flexDirection={'column'}
    >
      {isSmallScreen && photo && (
        <Box
          paddingBottom={{ _: 9, sm2: 10 }}
          marginX={FULL_BLEED_MARGIN_X}
        >
          <CoverPhoto
            websiteLayoutType={DesignLayoutType.aloha}
            photo={photo}
            pageId={pageId}
            pageType={pageType}
            pageSlug={pageSlug}
          />
        </Box>
      )}
      {graphicAccent}
      {title && (
        <GuestSiteTypographyOverride override={display3ToPageTitleOverride}>
          <Box style={chromeStyle}>
            <ResponsiveText
              typographyVariant="display3"
              defaultFontSize={36}
              marginTop={{ _: 0, sm2: 5 }}
              marginX={6}
              textAlign={'center'}
              wordBreak="break-word"
              text={title}
              skipResize={false}
              fontCategory={Category.PAGE_TITLE}
            />
          </Box>
        </GuestSiteTypographyOverride>
      )}
      {subtitle && (
        <GuestSiteTypographyOverride override={body4ToSubHeadingOverride}>
          <TextV2
            as="div"
            typographyVariant="body4"
            marginTop={7}
            marginX={'auto'}
            textAlign={'center'}
            maxWidth={CONTENT_BLOCK_MAX_WIDTH}
            wordBreak="break-word"
            color={pageFontColor || 'mono14'}
            style={chromeStyle}
          >
            <PrettyLink
              source={pageSlug}
              allowedTypes={['EMAIL']}
            >
              {subtitle}
            </PrettyLink>
          </TextV2>
        </GuestSiteTypographyOverride>
      )}
      <AccommodationsCountdownExperiment />
      {children}
    </Flex>
  ) : (
    <Box
      ref={pageRef}
      {...restProps}
      paddingX={CONTAINER_BLOCK_PADDING_X}
      marginX={'auto'}
    >
      {isSmallScreen && photo && (
        <Box
          paddingBottom={{ _: 9, sm2: 10 }}
          marginX={FULL_BLEED_MARGIN_X}
        >
          <CoverPhoto
            websiteLayoutType={DesignLayoutType.aloha}
            photo={photo}
            pageId={pageId}
            pageType={pageType}
            pageSlug={pageSlug}
          />
        </Box>
      )}
      {graphicAccent}
      {title ? (
        <GuestSiteTypographyOverride override={display3ToPageTitleOverride}>
          <StyledTitle
            typographyVariant="display3"
            marginTop={{ _: 0, sm2: 5 }}
            textAlign={'center'}
            style={chromeStyle}
          >
            {title}
          </StyledTitle>
        </GuestSiteTypographyOverride>
      ) : null}
      {subtitle ? (
        <GuestSiteTypographyOverride override={body4ToSubHeadingOverride}>
          <TextV2
            as="div"
            typographyVariant="body4"
            marginTop={7}
            marginX="auto"
            textAlign="center"
            maxWidth={CONTENT_BLOCK_MAX_WIDTH}
            wordBreak="break-word"
            style={chromeStyle}
          >
            {(() => {
              const plainSubtitle = <PrettyLink source={pageSlug}>{subtitle}</PrettyLink>;
              if (!isRegistryPage(pageType, pageSlug, isMigratedCustomPageEnabled)) return plainSubtitle;
              // SSR/client must agree: `isRichTextContent` is DOM-only and returns false on the server,
              // so wrap in NoSsr with the plain PrettyLink as fallback. SSR + first hydration emit the
              // fallback; the rich render swaps in after mount.
              return (
                <NoSsr fallback={plainSubtitle}>{isRichTextContent(subtitle) ? <LazyRichText richTextComponentData={{ id: pageId, text: subtitle }} /> : plainSubtitle}</NoSsr>
              );
            })()}
          </TextV2>
        </GuestSiteTypographyOverride>
      ) : null}
      {children}
    </Box>
  );
});
